import { useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import type { JobOrderSection } from '../../utils/jobOrder';
import { getSummarySegmentTextColor } from '../../utils/orderItem/helpers';
import SectionBar from './SectionBar';
import {
  PRINT_COLOR_ADJUST,
  PRINT_FIELD_ROW_GAP,
  PRINT_FONT_SIZE,
  cellPaddingSx,
  mutedLabelTypographySx,
  valueTypographySx,
} from './printStyles';

/** Floor so a crowded section stays readable when it cannot fit at full size. */
const SPEC_CONTENT_MIN_SCALE = 0.5;

interface SpecSectionProps {
  section: JobOrderSection;
}

const visibleFieldRoom = (slot: HTMLElement) => {
  const sectionEl = slot.parentElement;
  const bar = sectionEl?.firstElementChild;
  const grid = sectionEl?.parentElement;
  const gridRoom =
    grid && bar instanceof HTMLElement
      ? grid.clientHeight - bar.offsetHeight
      : 0;
  const rooms = [slot.clientHeight, gridRoom].filter((height) => height > 0);
  return rooms.length > 0 ? Math.min(...rooms) : 0;
};

const SpecSection = ({ section }: SpecSectionProps) => {
  const theme = useTheme();
  const slotRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const rowGapAtFullSize = theme.spacing(PRINT_FIELD_ROW_GAP);

  useLayoutEffect(() => {
    const slot = slotRef.current;
    const content = contentRef.current;
    if (!slot || !content) {
      return;
    }

    const gapPx = parseFloat(rowGapAtFullSize) || 0;

    const applyScale = (next: number) => {
      content.style.rowGap = `${gapPx * next}px`;
      content.querySelectorAll<HTMLElement>('.spec-label').forEach((el) => {
        el.style.fontSize = `${PRINT_FONT_SIZE.label * next}px`;
      });
      content.querySelectorAll<HTMLElement>('.spec-value').forEach((el) => {
        el.style.fontSize = `${PRINT_FONT_SIZE.value * next}px`;
      });
    };

    const fits = (next: number, available: number) => {
      applyScale(next);
      return content.offsetHeight <= available + 1;
    };

    const update = () => {
      const available = visibleFieldRoom(slot);
      if (available === 0) {
        return;
      }

      let next = 1;
      if (!fits(1, available)) {
        if (!fits(SPEC_CONTENT_MIN_SCALE, available)) {
          next = SPEC_CONTENT_MIN_SCALE;
        } else {
          let low = SPEC_CONTENT_MIN_SCALE;
          let high = 1;
          for (let step = 0; step < 8; step += 1) {
            const mid = (low + high) / 2;
            if (fits(mid, available)) {
              low = mid;
            } else {
              high = mid;
            }
          }
          next = low;
        }
      }

      applyScale(next);
      setScale((current) =>
        Math.abs(current - next) < 0.005 ? current : next
      );
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(slot);
    if (slot.parentElement?.parentElement) {
      observer.observe(slot.parentElement.parentElement);
    }
    window.addEventListener('beforeprint', update);
    const printMedia = window.matchMedia('print');
    printMedia.addEventListener('change', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('beforeprint', update);
      printMedia.removeEventListener('change', update);
    };
  }, [rowGapAtFullSize, section]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <SectionBar title={section.title} />
      <Box
        ref={slotRef}
        sx={{ flex: '1 1 0px', height: 0, minHeight: 0, overflow: 'hidden' }}
      >
        <Box
          ref={contentRef}
          sx={{
            ...cellPaddingSx,
            display: 'grid',
            gridTemplateColumns: 'max-content minmax(0, 1fr)',
            alignItems: 'baseline',
            columnGap: 1.5,
            rowGap: `calc(${rowGapAtFullSize} * ${scale})`,
          }}
        >
          {section.fields.map(({ label, value, colorHex }) => (
            <Box key={label} sx={{ display: 'contents' }}>
              <Typography
                className="spec-label"
                sx={{
                  ...mutedLabelTypographySx,
                  fontSize: PRINT_FONT_SIZE.label * scale,
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </Typography>
              <Typography
                className="spec-value"
                sx={{
                  ...valueTypographySx,
                  ...PRINT_COLOR_ADJUST,
                  fontSize: PRINT_FONT_SIZE.value * scale,
                  minWidth: 0,
                  overflowWrap: 'break-word',
                  color:
                    getSummarySegmentTextColor(theme, colorHex) ??
                    'text.primary',
                }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default SpecSection;
